/**
 * HRIS Job Editor — create and edit job postings.
 *
 * Two-column EN | FR layout per spec §12.3. Fields:
 *   - Slug (read-only on edit, lock+tooltip)
 *   - Bilingual title / summary / array-list sections
 *   - Structured Ville + Province/État + Pays + Télétravail
 *   - Type d'emploi EN/FR, Relève de EN/FR
 *   - Salaire, Statut, Visible, Date de publication (max=today), Date d'expiration
 *   - Inline bilingual completeness warnings
 *   - "Aperçu" button → job.atek.io/<lang>/careers/<slug>?preview=1
 *
 * Working language: French. All labels in French.
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import {
  Lock,
  Plus,
  Trash2,
  GripVertical,
  Calendar as CalendarIcon,
  ExternalLink,
  AlertTriangle,
  ArrowLeft,
} from 'lucide-react';
import { PageShell } from '@/shared/components';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import {
  useJobPosting,
  useCreateJobPosting,
  useUpdateJobPosting,
} from '../api/jobsApi';
import type {
  JobStatus,
  DepartmentIcon,
  RemoteType,
  CreateJobPostingInput,
  UpdateJobPostingInput,
} from '../api/jobsApi';

// ---------------------------------------------------------------------------
// Constants / Options
// ---------------------------------------------------------------------------

const PUBLIC_BASE_URL = 'https://job.atek.io';

const EMPLOYMENT_TYPES_EN = [
  'Full-time',
  'Part-time',
  'Contract',
  'Internship',
  'Temporary',
] as const;

const EMPLOYMENT_TYPES_FR: Record<string, string> = {
  'Full-time': 'Temps plein',
  'Part-time': 'Temps partiel',
  Contract: 'Contrat',
  Internship: 'Stage',
  Temporary: 'Temporaire',
};

const STATUS_OPTIONS: { value: JobStatus; label: string }[] = [
  { value: 'draft', label: 'Brouillon' },
  { value: 'open', label: 'Ouvert' },
  { value: 'closed', label: 'Fermé' },
];

const DEPARTMENT_ICON_OPTIONS: { value: DepartmentIcon; label: string }[] = [
  { value: 'operations', label: 'Opérations' },
  { value: 'sales', label: 'Ventes' },
];

// Radix <SelectItem> forbids value="" (reserved for clearing), so the "none"
// option uses a _none sentinel mapped back to '' at the Select boundary.
const REMOTE_TYPE_NONE = '_none';
const REMOTE_TYPE_OPTIONS: { value: RemoteType | typeof REMOTE_TYPE_NONE; label: string }[] = [
  { value: REMOTE_TYPE_NONE, label: '— Aucun —' },
  { value: 'onsite', label: 'En présentiel' },
  { value: 'hybrid', label: 'Hybride' },
  { value: 'fully_remote', label: 'Entièrement à distance' },
];

// Canadian provinces / US states subset (most common for ATEK postings)
const PROVINCE_OPTIONS = [
  { value: 'QC', label: 'Québec (QC)' },
  { value: 'ON', label: 'Ontario (ON)' },
  { value: 'BC', label: 'Colombie-Britannique (BC)' },
  { value: 'AB', label: 'Alberta (AB)' },
  { value: 'MB', label: 'Manitoba (MB)' },
  { value: 'SK', label: 'Saskatchewan (SK)' },
  { value: 'NS', label: 'Nouvelle-Écosse (NS)' },
  { value: 'NB', label: 'Nouveau-Brunswick (NB)' },
  { value: 'NL', label: 'Terre-Neuve (NL)' },
  { value: 'PE', label: 'Île-du-Prince-Édouard (PE)' },
  { value: 'NT', label: 'Territoires du Nord-Ouest (NT)' },
  { value: 'YT', label: 'Yukon (YT)' },
  { value: 'NU', label: 'Nunavut (NU)' },
  // US states (subset)
  { value: 'CA', label: 'California (CA)' },
  { value: 'NY', label: 'New York (NY)' },
  { value: 'TX', label: 'Texas (TX)' },
  { value: 'FL', label: 'Florida (FL)' },
  { value: 'WA', label: 'Washington (WA)' },
  { value: 'MA', label: 'Massachusetts (MA)' },
  { value: 'IL', label: 'Illinois (IL)' },
];

const COUNTRY_OPTIONS = [
  { value: 'CA', label: 'Canada (CA)' },
  { value: 'US', label: 'États-Unis (US)' },
];

// ---------------------------------------------------------------------------
// Form state shape
// ---------------------------------------------------------------------------

interface FormState {
  slug: string;
  titleEn: string;
  titleFr: string;
  departmentEn: string;
  departmentFr: string;
  departmentIcon: DepartmentIcon;
  city: string;
  state: string;
  country: string;
  remoteTypeVal: RemoteType | '';
  typeEn: string;
  typeFr: string;
  reportsToEn: string;
  reportsToFr: string;
  summaryEn: string;
  summaryFr: string;
  responsibilitiesEn: string[];
  responsibilitiesFr: string[];
  requirementsEn: string[];
  requirementsFr: string[];
  niceToHaveEn: string[];
  niceToHaveFr: string[];
  benefitsEn: string[];
  benefitsFr: string[];
  salary: string;
  status: JobStatus;
  visible: boolean;
  datePosted: string;
  expiresAt: string;
}

const EMPTY_FORM: FormState = {
  slug: '',
  titleEn: '',
  titleFr: '',
  departmentEn: '',
  departmentFr: '',
  departmentIcon: 'operations',
  city: '',
  state: 'QC',
  country: 'CA',
  remoteTypeVal: '',
  typeEn: 'Full-time',
  typeFr: 'Temps plein',
  reportsToEn: '',
  reportsToFr: '',
  summaryEn: '',
  summaryFr: '',
  responsibilitiesEn: [],
  responsibilitiesFr: [],
  requirementsEn: [],
  requirementsFr: [],
  niceToHaveEn: [],
  niceToHaveFr: [],
  benefitsEn: [],
  benefitsFr: [],
  salary: '',
  status: 'draft',
  visible: false,
  datePosted: new Date().toISOString().slice(0, 10),
  expiresAt: '',
};

// ---------------------------------------------------------------------------
// Bilingual-completeness warnings
// ---------------------------------------------------------------------------

type BilingualPair = {
  label: string;
  enKey: keyof FormState;
  frKey: keyof FormState;
};

const BILINGUAL_PAIRS: BilingualPair[] = [
  { label: 'Titre', enKey: 'titleEn', frKey: 'titleFr' },
  { label: 'Résumé', enKey: 'summaryEn', frKey: 'summaryFr' },
  { label: 'Département', enKey: 'departmentEn', frKey: 'departmentFr' },
  { label: 'Type d\'emploi', enKey: 'typeEn', frKey: 'typeFr' },
  { label: 'Relève de', enKey: 'reportsToEn', frKey: 'reportsToFr' },
  { label: 'Responsabilités', enKey: 'responsibilitiesEn', frKey: 'responsibilitiesFr' },
  { label: 'Exigences', enKey: 'requirementsEn', frKey: 'requirementsFr' },
  { label: 'Atouts', enKey: 'niceToHaveEn', frKey: 'niceToHaveFr' },
  { label: 'Avantages', enKey: 'benefitsEn', frKey: 'benefitsFr' },
];

function isFilled(v: FormState[keyof FormState]): boolean {
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === 'string') return v.trim().length > 0;
  return v !== null && v !== undefined;
}

function getCompletenessWarnings(form: FormState): string[] {
  const warnings: string[] = [];
  for (const { label, enKey, frKey } of BILINGUAL_PAIRS) {
    const enFilled = isFilled(form[enKey]);
    const frFilled = isFilled(form[frKey]);
    if (enFilled && !frFilled) warnings.push(`${label} : version FR manquante`);
    if (!enFilled && frFilled) warnings.push(`${label} : version EN manquante`);
  }
  return warnings;
}

// ---------------------------------------------------------------------------
// Array-list editor (responsibilities, requirements, etc.)
// ---------------------------------------------------------------------------

interface ArrayListEditorProps {
  items: string[];
  onChange: (items: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
}

function ArrayListEditor({ items, onChange, placeholder = 'Ajouter un élément…', disabled }: ArrayListEditorProps) {
  const addItem = () => onChange([...items, '']);

  const updateItem = (i: number, val: string) => {
    const next = [...items];
    next[i] = val;
    onChange(next);
  };

  const removeItem = (i: number) => {
    const next = [...items];
    next.splice(i, 1);
    onChange(next);
  };

  return (
    <div className="space-y-1.5">
      {items.map((item, i) => (
        <div key={i} className="flex items-center gap-1.5">
          <GripVertical className="h-4 w-4 text-muted-foreground shrink-0 cursor-grab" aria-hidden />
          <Input
            value={item}
            onChange={(e) => updateItem(i, e.target.value)}
            placeholder={placeholder}
            disabled={disabled}
            className="flex-1 h-8 text-sm"
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0"
            onClick={() => removeItem(i)}
            disabled={disabled}
            aria-label="Supprimer cet élément"
          >
            <Trash2 className="h-3.5 w-3.5 text-destructive" />
          </Button>
        </div>
      ))}
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-7 text-xs gap-1 mt-1"
        onClick={addItem}
        disabled={disabled}
      >
        <Plus className="h-3 w-3" />
        Ajouter
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Date picker — single date
// ---------------------------------------------------------------------------

interface SingleDatePickerProps {
  value: string; // ISO date yyyy-mm-dd or ''
  onChange: (val: string) => void;
  label: string;
  maxDate?: Date;
  disabled?: boolean;
  required?: boolean;
}

function SingleDatePicker({ value, onChange, label, maxDate, disabled, required }: SingleDatePickerProps) {
  const selected = value ? new Date(value + 'T12:00:00') : undefined;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className={cn('w-full justify-start text-left font-normal h-9', !value && 'text-muted-foreground')}
          disabled={disabled}
          aria-label={label}
          aria-required={required}
        >
          <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
          {value ? format(new Date(value + 'T12:00:00'), 'dd MMM yyyy') : 'Sélectionner une date'}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={selected}
          onSelect={(d) => onChange(d ? d.toISOString().slice(0, 10) : '')}
          disabled={(d) => !!maxDate && d > maxDate}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}

// ---------------------------------------------------------------------------
// Section separator for the two-column form
// ---------------------------------------------------------------------------

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <div className="col-span-2 mt-4 first:mt-0">
      <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide border-b pb-1 mb-3">
        {children}
      </h3>
    </div>
  );
}

// Column header labels
function ColHeader({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-xs font-semibold text-center text-muted-foreground uppercase tracking-wide pb-1 border-b mb-2">
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function JobEditor() {
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id && id !== 'new';
  const navigate = useNavigate();

  const { data: existing, isLoading } = useJobPosting(isEdit ? (id ?? '') : '');
  const createJob = useCreateJobPosting();
  const updateJob = useUpdateJobPosting();

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [previewLang, setPreviewLang] = useState<'fr' | 'en'>('fr');

  // Populate form when editing an existing job
  useEffect(() => {
    if (isEdit && existing) {
      setForm({
        slug: existing.slug,
        titleEn: existing.titleEn ?? '',
        titleFr: existing.titleFr ?? '',
        departmentEn: existing.departmentEn ?? '',
        departmentFr: existing.departmentFr ?? '',
        departmentIcon: existing.departmentIcon,
        city: existing.city ?? '',
        state: existing.state ?? 'QC',
        country: existing.country ?? 'CA',
        remoteTypeVal: existing.remoteTypeVal ?? '',
        typeEn: existing.typeEn ?? 'Full-time',
        typeFr: existing.typeFr ?? 'Temps plein',
        reportsToEn: existing.reportsToEn ?? '',
        reportsToFr: existing.reportsToFr ?? '',
        summaryEn: existing.summaryEn ?? '',
        summaryFr: existing.summaryFr ?? '',
        responsibilitiesEn: existing.responsibilitiesEn ?? [],
        responsibilitiesFr: existing.responsibilitiesFr ?? [],
        requirementsEn: existing.requirementsEn ?? [],
        requirementsFr: existing.requirementsFr ?? [],
        niceToHaveEn: existing.niceToHaveEn ?? [],
        niceToHaveFr: existing.niceToHaveFr ?? [],
        benefitsEn: existing.benefitsEn ?? [],
        benefitsFr: existing.benefitsFr ?? [],
        salary: existing.salary ?? '',
        status: existing.status,
        visible: existing.visible,
        datePosted: existing.datePosted
          ? existing.datePosted.slice(0, 10)
          : new Date().toISOString().slice(0, 10),
        expiresAt: existing.expiresAt ? existing.expiresAt.slice(0, 10) : '',
      });
    }
  }, [isEdit, existing]);

  const set = useCallback(
    <K extends keyof FormState>(key: K, value: FormState[K]) => {
      setForm((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  const warnings = getCompletenessWarnings(form);
  const isPending = createJob.isPending || updateJob.isPending;

  // When employment type EN changes, auto-set the FR equivalent
  const handleTypeEnChange = (val: string) => {
    set('typeEn', val);
    const frEquiv = EMPLOYMENT_TYPES_FR[val];
    if (frEquiv) set('typeFr', frEquiv);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const payload: CreateJobPostingInput = {
      slug: form.slug,
      titleEn: form.titleEn,
      titleFr: form.titleFr,
      departmentEn: form.departmentEn || null,
      departmentFr: form.departmentFr || null,
      departmentIcon: form.departmentIcon,
      city: form.city,
      state: form.state,
      country: form.country,
      remoteTypeVal: form.remoteTypeVal || null,
      typeEn: form.typeEn,
      typeFr: form.typeFr,
      reportsToEn: form.reportsToEn || null,
      reportsToFr: form.reportsToFr || null,
      summaryEn: form.summaryEn,
      summaryFr: form.summaryFr,
      responsibilitiesEn: form.responsibilitiesEn,
      responsibilitiesFr: form.responsibilitiesFr,
      requirementsEn: form.requirementsEn,
      requirementsFr: form.requirementsFr,
      niceToHaveEn: form.niceToHaveEn,
      niceToHaveFr: form.niceToHaveFr,
      benefitsEn: form.benefitsEn,
      benefitsFr: form.benefitsFr,
      salary: form.salary || null,
      status: form.status,
      visible: form.visible,
      datePosted: form.datePosted ? new Date(form.datePosted + 'T12:00:00').toISOString() : new Date().toISOString(),
      expiresAt: form.expiresAt ? new Date(form.expiresAt + 'T23:59:59').toISOString() : null,
    };

    if (isEdit && id) {
      const updatePayload: UpdateJobPostingInput = { ...payload };
      // slug is immutable on edit — the server rejects changes; omit it
      delete (updatePayload as Record<string, unknown>).slug;
      updateJob.mutate(
        { id, data: updatePayload },
        { onSuccess: () => navigate('/jobs') }
      );
    } else {
      createJob.mutate(payload, {
        onSuccess: () => navigate('/jobs'),
      });
    }
  };

  const today = new Date();

  const publicPageUrl = form.slug
    ? `${PUBLIC_BASE_URL}/${previewLang}/careers/${form.slug}?preview=1`
    : null;

  if (isEdit && isLoading) {
    return (
      <PageShell>
        <div className="flex items-center justify-center py-20 text-muted-foreground text-sm">
          Chargement…
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell
      actions={
        <div className="flex items-center gap-2">
          {/* Preview button */}
          {publicPageUrl && (
            <div className="flex items-center gap-1 border rounded-md overflow-hidden h-9">
              <Select
                value={previewLang}
                onValueChange={(v) => { if (v) setPreviewLang(v as 'fr' | 'en'); }}
              >
                <SelectTrigger className="h-9 w-20 border-0 rounded-none text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="fr">FR</SelectItem>
                  <SelectItem value="en">EN</SelectItem>
                </SelectContent>
              </Select>
              <a
                href={publicPageUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 px-3 h-9 text-xs font-medium border-l hover:bg-muted transition-colors"
              >
                <ExternalLink className="h-3.5 w-3.5" />
                Aperçu
              </a>
            </div>
          )}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => navigate('/jobs')}
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Retour
          </Button>
          <Button type="submit" form="job-editor-form" size="sm" disabled={isPending}>
            {isPending ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </div>
      }
    >
      {/* Bilingual completeness warnings */}
      {warnings.length > 0 && (
        <div className="rounded-md border border-yellow-200 bg-yellow-50 px-4 py-3 space-y-1">
          <div className="flex items-center gap-2 text-yellow-800 text-sm font-medium">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            Complétude bilingue
          </div>
          {warnings.map((w, i) => (
            <p key={i} className="text-xs text-yellow-700 ml-6">
              {w}
            </p>
          ))}
        </div>
      )}

      <form id="job-editor-form" onSubmit={handleSubmit} className="space-y-0">
        {/* Sticky status bar */}
        <div className="sticky top-0 z-10 bg-background border-b mb-4 -mx-4 px-4 py-2 flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Label htmlFor="status-select" className="text-xs font-medium">Statut</Label>
            <Select
              value={form.status}
              onValueChange={(v) => { if (v) set('status', v as JobStatus); }}
            >
              <SelectTrigger id="status-select" className="h-8 w-32 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor="visible-switch" className="text-xs font-medium">Visible</Label>
            <Switch
              id="visible-switch"
              checked={form.visible}
              onCheckedChange={(v) => set('visible', v)}
            />
          </div>
        </div>

        {/* Two-column EN | FR grid */}
        <div className="grid grid-cols-2 gap-x-6 gap-y-3">
          {/* Column headers */}
          <ColHeader>EN — Anglais</ColHeader>
          <ColHeader>FR — Français</ColHeader>

          {/* ── Identité ── */}
          <SectionTitle>Identité du poste</SectionTitle>

          {/* Slug (shared, spans both columns for create; locked on edit) */}
          <div className="col-span-2">
            <Label htmlFor="slug" className="text-xs">
              Slug (URL)
              {!isEdit && <span className="text-destructive ml-1">*</span>}
            </Label>
            {isEdit ? (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center gap-2 mt-1">
                      <Input
                        id="slug"
                        value={form.slug}
                        readOnly
                        disabled
                        className="max-w-xs font-mono text-sm opacity-70 bg-muted"
                      />
                      <Lock className="h-4 w-4 text-muted-foreground" aria-hidden />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="max-w-xs">
                    URL & identité Indeed permanentes — ne peut pas être modifié après création.
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : (
              <Input
                id="slug"
                value={form.slug}
                onChange={(e) => set('slug', e.target.value)}
                placeholder="account-manager"
                className="max-w-xs font-mono text-sm mt-1"
                required
              />
            )}
          </div>

          {/* Titre */}
          <div>
            <Label htmlFor="titleEn" className="text-xs">Titre EN <span className="text-destructive">*</span></Label>
            <Input
              id="titleEn"
              value={form.titleEn}
              onChange={(e) => set('titleEn', e.target.value)}
              placeholder="Account Manager"
              className="mt-1"
              required
            />
          </div>
          <div>
            <Label htmlFor="titleFr" className="text-xs">Titre FR <span className="text-destructive">*</span></Label>
            <Input
              id="titleFr"
              value={form.titleFr}
              onChange={(e) => set('titleFr', e.target.value)}
              placeholder="Gestionnaire de comptes"
              className="mt-1"
              required
            />
          </div>

          {/* Département */}
          <div>
            <Label htmlFor="departmentEn" className="text-xs">Département EN</Label>
            <Input
              id="departmentEn"
              value={form.departmentEn}
              onChange={(e) => set('departmentEn', e.target.value)}
              placeholder="Sales"
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="departmentFr" className="text-xs">Département FR</Label>
            <Input
              id="departmentFr"
              value={form.departmentFr}
              onChange={(e) => set('departmentFr', e.target.value)}
              placeholder="Ventes"
              className="mt-1"
            />
          </div>

          {/* Icône département — shared */}
          <div className="col-span-2">
            <Label htmlFor="deptIcon" className="text-xs">Icône département <span className="text-destructive">*</span></Label>
            <Select
              value={form.departmentIcon}
              onValueChange={(v) => { if (v) set('departmentIcon', v as DepartmentIcon); }}
            >
              <SelectTrigger id="deptIcon" className="mt-1 w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DEPARTMENT_ICON_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* ── Localisation ── */}
          <SectionTitle>Localisation</SectionTitle>

          {/* Ville — shared */}
          <div className="col-span-2">
            <Label htmlFor="city" className="text-xs">Ville <span className="text-destructive">*</span></Label>
            <Input
              id="city"
              value={form.city}
              onChange={(e) => set('city', e.target.value)}
              placeholder="Saint-Laurent"
              className="mt-1 max-w-xs"
              required
            />
          </div>

          {/* Province + Pays */}
          <div>
            <Label htmlFor="state" className="text-xs">Province / État <span className="text-destructive">*</span></Label>
            <Select
              value={form.state}
              onValueChange={(v) => { if (v) set('state', v); }}
            >
              <SelectTrigger id="state" className="mt-1">
                <SelectValue placeholder="Sélectionner…" />
              </SelectTrigger>
              <SelectContent>
                {PROVINCE_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="country" className="text-xs">Pays <span className="text-destructive">*</span></Label>
            <Select
              value={form.country}
              onValueChange={(v) => { if (v) set('country', v); }}
            >
              <SelectTrigger id="country" className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {COUNTRY_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Télétravail — shared */}
          <div className="col-span-2">
            <Label htmlFor="remoteType" className="text-xs">Télétravail</Label>
            <Select
              value={form.remoteTypeVal || REMOTE_TYPE_NONE}
              onValueChange={(v) => set('remoteTypeVal', v === REMOTE_TYPE_NONE ? '' : (v as RemoteType))}
            >
              <SelectTrigger id="remoteType" className="mt-1 w-56">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {REMOTE_TYPE_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* ── Type d'emploi ── */}
          <SectionTitle>Type d'emploi</SectionTitle>

          <div>
            <Label htmlFor="typeEn" className="text-xs">Type EN <span className="text-destructive">*</span></Label>
            <Select
              value={form.typeEn}
              onValueChange={handleTypeEnChange}
            >
              <SelectTrigger id="typeEn" className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {EMPLOYMENT_TYPES_EN.map((t) => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="typeFr" className="text-xs">Type FR <span className="text-destructive">*</span></Label>
            <Input
              id="typeFr"
              value={form.typeFr}
              onChange={(e) => set('typeFr', e.target.value)}
              placeholder="Temps plein"
              className="mt-1"
              required
            />
          </div>

          {/* Relève de */}
          <div>
            <Label htmlFor="reportsToEn" className="text-xs">Relève de EN</Label>
            <Input
              id="reportsToEn"
              value={form.reportsToEn}
              onChange={(e) => set('reportsToEn', e.target.value)}
              placeholder="VP Sales"
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="reportsToFr" className="text-xs">Relève de FR</Label>
            <Input
              id="reportsToFr"
              value={form.reportsToFr}
              onChange={(e) => set('reportsToFr', e.target.value)}
              placeholder="VP Ventes"
              className="mt-1"
            />
          </div>

          {/* ── Contenu ── */}
          <SectionTitle>Contenu du poste</SectionTitle>

          {/* Résumé */}
          <div>
            <Label htmlFor="summaryEn" className="text-xs">Résumé EN <span className="text-destructive">*</span></Label>
            <Textarea
              id="summaryEn"
              value={form.summaryEn}
              onChange={(e) => set('summaryEn', e.target.value)}
              rows={4}
              className="mt-1 resize-y"
              required
            />
          </div>
          <div>
            <Label htmlFor="summaryFr" className="text-xs">Résumé FR <span className="text-destructive">*</span></Label>
            <Textarea
              id="summaryFr"
              value={form.summaryFr}
              onChange={(e) => set('summaryFr', e.target.value)}
              rows={4}
              className="mt-1 resize-y"
              required
            />
          </div>

          {/* Responsabilités */}
          <div>
            <Label className="text-xs">Responsabilités EN</Label>
            <div className="mt-1">
              <ArrayListEditor
                items={form.responsibilitiesEn}
                onChange={(v) => set('responsibilitiesEn', v)}
                disabled={isPending}
              />
            </div>
          </div>
          <div>
            <Label className="text-xs">Responsabilités FR</Label>
            <div className="mt-1">
              <ArrayListEditor
                items={form.responsibilitiesFr}
                onChange={(v) => set('responsibilitiesFr', v)}
                disabled={isPending}
              />
            </div>
          </div>

          {/* Exigences */}
          <div>
            <Label className="text-xs">Exigences EN</Label>
            <div className="mt-1">
              <ArrayListEditor
                items={form.requirementsEn}
                onChange={(v) => set('requirementsEn', v)}
                disabled={isPending}
              />
            </div>
          </div>
          <div>
            <Label className="text-xs">Exigences FR</Label>
            <div className="mt-1">
              <ArrayListEditor
                items={form.requirementsFr}
                onChange={(v) => set('requirementsFr', v)}
                disabled={isPending}
              />
            </div>
          </div>

          {/* Atouts */}
          <div>
            <Label className="text-xs">Atouts EN</Label>
            <div className="mt-1">
              <ArrayListEditor
                items={form.niceToHaveEn}
                onChange={(v) => set('niceToHaveEn', v)}
                disabled={isPending}
              />
            </div>
          </div>
          <div>
            <Label className="text-xs">Atouts FR</Label>
            <div className="mt-1">
              <ArrayListEditor
                items={form.niceToHaveFr}
                onChange={(v) => set('niceToHaveFr', v)}
                disabled={isPending}
              />
            </div>
          </div>

          {/* Avantages */}
          <div>
            <Label className="text-xs">Avantages EN</Label>
            <div className="mt-1">
              <ArrayListEditor
                items={form.benefitsEn}
                onChange={(v) => set('benefitsEn', v)}
                disabled={isPending}
              />
            </div>
          </div>
          <div>
            <Label className="text-xs">Avantages FR</Label>
            <div className="mt-1">
              <ArrayListEditor
                items={form.benefitsFr}
                onChange={(v) => set('benefitsFr', v)}
                disabled={isPending}
              />
            </div>
          </div>

          {/* ── Conditions ── */}
          <SectionTitle>Conditions & publication</SectionTitle>

          {/* Salaire — shared */}
          <div className="col-span-2">
            <Label htmlFor="salary" className="text-xs">Salaire</Label>
            <Input
              id="salary"
              value={form.salary}
              onChange={(e) => set('salary', e.target.value)}
              placeholder="$70 000 per year"
              className="mt-1 max-w-xs"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Format Indeed : "$X per year". Laisser vide pour omettre du feed.
            </p>
          </div>

          {/* Date de publication */}
          <div>
            <Label className="text-xs">Date de publication <span className="text-destructive">*</span></Label>
            <div className="mt-1">
              <SingleDatePicker
                label="Date de publication"
                value={form.datePosted}
                onChange={(v) => set('datePosted', v)}
                maxDate={today}
                required
              />
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Doit être aujourd'hui ou dans le passé (validé côté serveur).
            </p>
          </div>

          {/* Date d'expiration */}
          <div>
            <Label className="text-xs">Date d'expiration</Label>
            <div className="mt-1">
              <SingleDatePicker
                label="Date d'expiration"
                value={form.expiresAt}
                onChange={(v) => set('expiresAt', v)}
              />
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Optionnel. Indeed retire le poste à cette date.
            </p>
          </div>
        </div>

        {/* Submit footer */}
        <div className="flex justify-end gap-2 pt-6 border-t mt-6">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/jobs')}
          >
            Annuler
          </Button>
          <Button type="submit" disabled={isPending}>
            {isPending ? 'Enregistrement…' : isEdit ? 'Mettre à jour' : 'Créer le poste'}
          </Button>
        </div>
      </form>
    </PageShell>
  );
}
